import type {
  AuthContact,
  AuthResourceContact,
  Contact,
  MemberContact,
  UserContact,
  WithCollapsedMemberContact,
  WithCollapsedAuthContact,
  WithCollapsedAuthResourceContact,
  WithCollapsedUserContact,
  UnknownContact
} from './contacts';
import { collapseByMemberEmail } from './collapse-by-member-email';
import { collapseDuplicateAuthContacts } from './collapse-duplicate-auth-contacts';
import { collapseUserToAuth } from './collapse-user-to-auth';
import {
  isAuth,
  isAuthResource,
  isMember,
  isUser
} from './contacts';

export type DividedContacts = {
  members: MemberContact[];
  authContacts: AuthContact[];
  authResourceContacts: AuthResourceContact[];
  userContacts: UserContact[];
  unknownContacts: UnknownContact[];
};

export type DividedCollapsedContacts = {
  members: WithCollapsedMemberContact[];
  authContacts: WithCollapsedAuthContact[];
  authResourceContacts: WithCollapsedAuthResourceContact[];
  userContacts: WithCollapsedUserContact[];
  unknownContacts: UnknownContact[];
};

function divideByContactType(contacts: Contact[]): DividedContacts {
  const members = [];
  const authContacts = [];
  const authResourceContacts = [];
  const userContacts = [];
  const unknownContacts = [];

  for (const contact of contacts) {
    if (isMember(contact)) {
      members.push(contact);
    } else if (isAuth(contact)) {
      authContacts.push(contact);
    } else if (isUser(contact)) {
      userContacts.push(contact);
    } else if (isAuthResource(contact)) {
      authResourceContacts.push(contact);
    } else {
      unknownContacts.push(contact);
    }
  }

  return {
    members,
    authContacts,
    authResourceContacts,
    userContacts,
    unknownContacts
  };
}

function withEmptyCollapsedContacts(contacts: DividedContacts): DividedCollapsedContacts {
  function addEmptyCollapsedContacts<C extends Contact>(contacts: C[]) {
    return contacts.map((contact) => ({
      ...contact,
      collapsedContacts: []
    }));
  }

  return {
    members: addEmptyCollapsedContacts(contacts.members),
    authContacts: addEmptyCollapsedContacts(contacts.authContacts),
    userContacts: addEmptyCollapsedContacts(contacts.userContacts),
    authResourceContacts: addEmptyCollapsedContacts(contacts.authResourceContacts),
    unknownContacts: addEmptyCollapsedContacts(contacts.unknownContacts)
  };
}

function mergeContacts(dividedContacts: DividedContacts): Contact[] {
  return [...Object.values(dividedContacts).flat()];
}

export function collapseContacts(contacts: Contact[] | null | undefined) {
  if (!Array.isArray(contacts) || contacts.length === 0) {
    return [];
  }

  const dividedContacts = divideByContactType(contacts);
  const dividedWithEmptyCollapsedContacts = withEmptyCollapsedContacts(dividedContacts);

  const collapsedByMemberEmail = collapseByMemberEmail(dividedWithEmptyCollapsedContacts);
  const collapsedToAuth = collapseUserToAuth(collapsedByMemberEmail);
  const collapsedAuthDuplicates = collapseDuplicateAuthContacts(collapsedToAuth);

  return mergeContacts(collapsedAuthDuplicates);
}
